import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip.js";
import { useAppearance } from "./appearance-provider.js";
import { cn } from "../lib/cn.js";

/**
 * Sidebar primitives
 * ------------------
 * Mirrors the Shadcnblocks Admin Kit pattern from the reference
 * screenshots: ~240px expanded, ~64px in icon mode, brand block at top,
 * grouped navigation with uppercase muted labels, filled-pill active
 * state, optional submenu chevron, user footer.
 *
 * The collapsed/icon mode is driven by the `data-sidebar` attribute on
 * <html> (set by AppearanceProvider). Item labels and group titles
 * collapse via CSS, not by remounting — so route navigation never
 * triggers a sidebar layout shift.
 */

interface SidebarProps extends React.HTMLAttributes<HTMLElement> {}

export const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  ({ className, children, ...props }, ref) => {
    const { sidebar } = useAppearance();
    return (
      <aside
        ref={ref}
        data-mode={sidebar}
        className={cn(
          // Hide entirely below `md` — the MobileBottomNav primitive
          // owns navigation on small viewports. `flex` on md+ restores
          // the column layout.
          // Sticky to viewport so the profile footer stays pinned at the
          // bottom-left as the main content scrolls (GitHub / Linear /
          // Vercel pattern). `sticky top-0` works because the parent
          // (`AppShell`) is `flex min-h-svh` — the sidebar is a flex
          // child whose own scroll context is decoupled from the page's.
          // `--app-top-inset` is the height of an app-level banner pinned
          // above the shell (IA support-session banner); 0 otherwise.
          "group/sidebar border-border bg-card/40 sticky top-[var(--app-top-inset,0px)] hidden h-[calc(100svh_-_var(--app-top-inset,0px))] shrink-0 flex-col gap-1 self-start border-r transition-[width] duration-200 ease-out md:flex",
          "data-[mode=default]:w-[240px] data-[mode=icon]:w-[64px]",
          className,
        )}
        {...props}
      >
        <TooltipProvider delayDuration={120}>{children}</TooltipProvider>
      </aside>
    );
  },
);
Sidebar.displayName = "Sidebar";

export function SidebarHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex h-14 shrink-0 items-center gap-2 px-3", className)} {...props}>
      {children}
    </div>
  );
}

export function SidebarBrand({
  icon,
  title,
  subtitle,
  tile = "primary",
}: {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  /**
   * `primary` (default) sits the icon on the palette primary colour;
   * `neutral` uses a card-coloured tile for full-colour marks such as
   * the EduViaOS logo.
   */
  tile?: "primary" | "neutral";
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-md shadow-sm",
          tile === "primary"
            ? "bg-primary text-primary-foreground"
            : "bg-card text-foreground border-border/60 border",
        )}
      >
        {icon ?? <span className="bg-primary-foreground block size-2 rounded-full" />}
      </span>
      <div className="min-w-0 group-data-[mode=icon]/sidebar:hidden">
        <p className="truncate text-sm font-semibold leading-tight">{title}</p>
        {subtitle ? (
          <p className="text-muted-foreground truncate text-[11px] leading-tight">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

export function SidebarBody({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <nav
      aria-label="Primary"
      className={cn("flex-1 overflow-y-auto px-2 py-2", className)}
      {...props}
    >
      {children}
    </nav>
  );
}

export function SidebarGroup({
  label,
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { label?: string }) {
  return (
    <div className={cn("mb-2", className)} {...props}>
      {label ? (
        <p className="text-muted-foreground/70 mb-1 px-2 text-[11px] font-medium uppercase tracking-wide group-data-[mode=icon]/sidebar:hidden">
          {label}
        </p>
      ) : null}
      <ul className="grid gap-0.5">{children}</ul>
    </div>
  );
}

export interface SidebarItemProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  icon?: React.ReactNode;
  label: string;
  active?: boolean;
  hasSubmenu?: boolean;
  /** Custom render for the anchor (e.g. react-router NavLink). */
  asChild?: boolean;
  children?: React.ReactNode;
}

/**
 * Render an `<a>` styled as a sidebar nav item. To use react-router's
 * <NavLink> (which controls `active` via render prop), wrap your own
 * NavLink and pass its props through.
 */
export const SidebarItem = React.forwardRef<HTMLAnchorElement, SidebarItemProps>(
  ({ icon, label, active, hasSubmenu, className, ...props }, ref) => {
    const inner = (
      <a
        ref={ref}
        data-active={active ? "true" : undefined}
        className={cn(
          "group/item relative flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
          "text-muted-foreground hover:bg-muted hover:text-foreground",
          "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2",
          "data-[active=true]:bg-muted data-[active=true]:text-foreground data-[active=true]:font-medium",
          "group-data-[mode=icon]/sidebar:justify-center group-data-[mode=icon]/sidebar:px-0",
          className,
        )}
        {...props}
      >
        {icon ? (
          <span className="grid size-4 shrink-0 place-items-center [&_svg]:size-4">{icon}</span>
        ) : null}
        <span className="flex-1 truncate group-data-[mode=icon]/sidebar:hidden">{label}</span>
        {hasSubmenu ? (
          <ChevronRight
            className="text-muted-foreground/60 size-3.5 shrink-0 group-data-[mode=icon]/sidebar:hidden"
            aria-hidden="true"
          />
        ) : null}
      </a>
    );

    return (
      <li>
        <Tooltip>
          <TooltipTrigger asChild>{inner}</TooltipTrigger>
          {/* Tooltip only visible in icon mode (label is the tooltip content). */}
          <TooltipContent side="right" className="hidden group-data-[mode=icon]/sidebar:block">
            {label}
          </TooltipContent>
        </Tooltip>
      </li>
    );
  },
);
SidebarItem.displayName = "SidebarItem";

export function SidebarFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("border-border shrink-0 border-t px-3 py-3", className)} {...props}>
      {children}
    </div>
  );
}

export interface SidebarUserProps {
  name: string;
  email: string;
  initials?: string;
  menuTrigger?: React.ReactNode;
}

/**
 * The bottom-of-sidebar user card. Caller wires their own DropdownMenu
 * around the menuTrigger if they want the 3-dot menu (sign out, etc).
 */
export function SidebarUser({ name, email, initials, menuTrigger }: SidebarUserProps) {
  const fallback = initials ?? name.slice(0, 2).toUpperCase();
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <div
        aria-hidden="true"
        className="bg-primary/15 text-primary grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold"
      >
        {fallback}
      </div>
      <div className="min-w-0 flex-1 group-data-[mode=icon]/sidebar:hidden">
        <p className="truncate text-sm font-medium leading-tight">{name}</p>
        <p className="text-muted-foreground truncate text-xs leading-tight">{email}</p>
      </div>
      {menuTrigger ? (
        <div className="shrink-0 group-data-[mode=icon]/sidebar:hidden">{menuTrigger}</div>
      ) : null}
    </div>
  );
}
